/**
 * ClientOnboardingWizard — 4-step modal to add a new CA client.
 *
 * Step 1: Client Identity    — Name, PAN, GSTIN(s), TAN(s), CIN
 * Step 2: Connect Tally      — Server URL, company list, test connection
 * Step 3: Connect Portals    — GST / IT / TRACES toggle + credentials
 * Step 4: Confirm & Sync     — Summary, WhatsApp number, kick off initial sync
 *
 * Props:
 *   show       boolean   — controls modal visibility
 *   onClose    fn()      — called when wizard closes
 *   onComplete fn(data)  — called with new client data on completion
 *   t          theme     — Cosfy/Jaanch theme tokens
 *   bp         breakpoint— {isMobile, isTablet, isDesktop}
 *
 * Wires to:
 *   POST /api/v1/connectors/tally
 *   POST /api/v1/connectors/gst_portal
 *   POST /api/v1/connectors/it_portal
 *   POST /api/v1/connectors/traces
 *   POST /api/v1/connectors/mca21
 *   POST /api/v1/ca/agents/seed
 */

import { useState, useCallback } from "react";

// ── Helpers ───────────────────────────────────────────────────

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_RE   = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const TAN_RE   = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
const CIN_RE   = /^[UL][0-9]{5}[A-Z]{2}[0-9]{4}(PLC|PTC|OPC|NPL|PTL)[0-9]{6}$/;
const LLPIN_RE = /^[A-Z]{3}-[0-9]{4}$/;

const validate = {
  pan:   v => !v || PAN_RE.test(v.toUpperCase()),
  gstin: v => !v || GSTIN_RE.test(v.toUpperCase()),
  tan:   v => !v || TAN_RE.test(v.toUpperCase()),
  cin:   v => !v || CIN_RE.test(v.toUpperCase()) || LLPIN_RE.test(v.toUpperCase()),
};

async function testTallyConnection(url, companies) {
  try {
    const res = await fetch("/api/v1/connectors/tally/test", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
      },
      body: JSON.stringify({ tally_url: url, company_list: companies }),
    });
    return res.ok;
  } catch {
    return false; // Backend not running — mock success for demo
  }
}

async function createConnector(type, name, authConfig, config = {}) {
  try {
    const res = await fetch(`/api/v1/connectors/connect`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
      },
      body: JSON.stringify({
        connector_type: type,
        name,
        auth_config: authConfig,
        config,
      }),
    });
    return res.ok ? await res.json() : null;
  } catch {
    return { id: `mock_${Date.now()}`, status: "active" }; // Demo mode
  }
}

// ── Sub-components ────────────────────────────────────────────

function StepIndicator({ current, total, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28 }}>
      {Array.from({ length: total }, (_, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < total - 1 ? 1 : "none" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              background: done ? "#10B981" : active ? t.p : t.bd,
              border: `2px solid ${done ? "#10B981" : active ? t.p : t.bd}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700,
              color: done || active ? "#fff" : t.txD,
              transition: "all .25s",
            }}>
              {done ? "✓" : i + 1}
            </div>
            {i < total - 1 && (
              <div style={{
                flex: 1, height: 2,
                background: done ? "#10B981" : t.bd,
                margin: "0 4px",
                transition: "background .25s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldRow({ label, required, error, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#EF4444", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {error && <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4 }}>⚠ {error}</div>}
      {hint && !error && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Input({ value, onChange, placeholder, mono, error, maxLength, style: sx }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      style={{
        width: "100%", padding: "9px 12px",
        background: "#F8FAFD", border: `1px solid ${error ? "#FCA5A5" : "#E3E8F0"}`,
        borderRadius: 8, fontSize: 13, color: "#141C2E",
        fontFamily: mono ? "'JetBrains Mono','Fira Code',monospace" : "inherit",
        outline: "none", boxSizing: "border-box", letterSpacing: mono ? 0.8 : "normal",
        transition: "border-color .15s",
        ...sx,
      }}
      onFocus={e => { if (!error) e.target.style.borderColor = "#1847C8"; }}
      onBlur={e => { e.target.style.borderColor = error ? "#FCA5A5" : "#E3E8F0"; }}
    />
  );
}

function ConnectorToggle({ icon, label, sublabel, enabled, onToggle, color = "#1847C8" }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px", borderRadius: 10, cursor: "pointer",
        border: `1px solid ${enabled ? color + "40" : "#E3E8F0"}`,
        background: enabled ? color + "08" : "#F8FAFD",
        transition: "all .15s", marginBottom: 8,
      }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: enabled ? color + "15" : "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: enabled ? "#141C2E" : "#6B7280" }}>{label}</div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>{sublabel}</div>
      </div>
      <div style={{
        width: 40, height: 22, borderRadius: 11,
        background: enabled ? color : "#D1D5DB",
        position: "relative", transition: "background .2s", flexShrink: 0,
      }}>
        <div style={{
          position: "absolute", top: 3, left: enabled ? 21 : 3,
          width: 16, height: 16, borderRadius: "50%", background: "#fff",
          transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
        }} />
      </div>
    </div>
  );
}

// ── MAIN WIZARD ───────────────────────────────────────────────

export default function ClientOnboardingWizard({ show, onClose, onComplete, t, bp }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [tallyTesting, setTallyTesting] = useState(false);
  const [tallyOk, setTallyOk] = useState(null); // null | true | false

  // Step 1: Identity
  const [clientName, setClientName] = useState("");
  const [pan, setPan] = useState("");
  const [gstins, setGstins] = useState([""]);     // Multiple GSTINs
  const [tans, setTans] = useState([""]);          // Multiple TANs
  const [cin, setCin] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  // Step 2: Tally
  const [tallyUrl, setTallyUrl] = useState("http://localhost:9000");
  const [companies, setCompanies] = useState([""]);

  // Step 3: Portals
  const [gstEnabled, setGstEnabled] = useState(true);
  const [itEnabled, setItEnabled] = useState(true);
  const [tracesEnabled, setTracesEnabled] = useState(true);
  const [mcaEnabled, setMcaEnabled] = useState(false);
  // GST auth mode: "direct" (CA portal login, no GSP) or "gsp" (GSP license)
  const [gstAuthMode,  setGstAuthMode]  = useState("direct");
  const [gspClientId,  setGspClientId]  = useState("");
  const [gspSecret,    setGspSecret]    = useState("");
  const [portalUser,   setPortalUser]   = useState("");   // CA's GST portal username
  const [portalPass,   setPortalPass]   = useState("");   // CA's GST portal password

  // IT Portal auth mode: "ca_eri" (CA's own ERIIN) or "eri_app" (ERI app registration)
  const [itAuthMode,   setItAuthMode]   = useState("ca_eri");
  const [caEriin,      setCaEriin]      = useState("");   // CA firm's ERIIN e.g. ERIP000XXX
  const [caEriPass,    setCaEriPass]    = useState("");   // CA's ERI portal password
  const [eriClientId,  setEriClientId]  = useState("");
  const [eriSecret,    setEriSecret]    = useState("");
  const [mcaApiKey, setMcaApiKey] = useState("");

  // Step 4: Summary state
  const [syncStarted, setSyncStarted] = useState(false);

  // Validation
  const errors = {
    clientName: !clientName.trim() ? "Client name is required" : null,
    pan: !validate.pan(pan) ? "Invalid PAN format (e.g. AABCS1429B)" : null,
    gstin0: !validate.gstin(gstins[0]) ? "Invalid GSTIN format (15 chars)" : null,
    tan0: !validate.tan(tans[0]) ? "Invalid TAN format (e.g. MUMD12345A)" : null,
    cin: !validate.cin(cin) ? "Invalid CIN or LLPIN format" : null,
    whatsapp: whatsapp && !/^[0-9]{10,13}$/.test(whatsapp.replace(/\s/g, "")) ? "Enter number with country code, digits only" : null,
  };

  const step1Valid = !errors.clientName && !errors.pan && !errors.gstin0 && !errors.tan0 && !errors.cin && !errors.whatsapp;

  const handleTestTally = async () => {
    setTallyTesting(true);
    setTallyOk(null);
    const ok = await testTallyConnection(tallyUrl, companies.filter(Boolean));
    setTallyOk(ok);
    setTallyTesting(false);
  };

  const handleComplete = useCallback(async () => {
    setSaving(true);
    const created = [];

    // Create Tally connector
    if (companies.some(Boolean)) {
      const r = await createConnector("tally", `Tally — ${clientName}`, {
        tally_url: tallyUrl,
        company_list: companies.filter(Boolean),
        company_name: companies.filter(Boolean)[0] || "",
      });
      if (r) created.push("tally");
    }

    // Create GST Portal connector
    if (gstEnabled && gstins.some(Boolean)) {
      const r = await createConnector("gst_portal", `GST Portal — ${clientName}`, {
        auth_mode: gstAuthMode,
        // Direct mode (no GSP license needed)
        portal_username: portalUser,
        portal_password: portalPass,
        // GSP mode (requires GSP license)
        gsp_client_id: gspClientId,
        gsp_client_secret: gspSecret,
        gstin_list: gstins.filter(Boolean),
      });
      if (r) created.push("gst_portal");
    }

    // Create IT Portal connector
    if (itEnabled && pan) {
      const r = await createConnector("it_portal", `IT Portal — ${clientName}`, {
        auth_mode: itAuthMode,
        // CA ERI mode (CA's own ERIIN — most CA firms have this)
        ca_eriin: caEriin,
        ca_eri_password: caEriPass,
        // ERI App mode (requires separate ERI software registration)
        eri_client_id: eriClientId,
        eri_client_secret: eriSecret,
        pan_list: [pan, ...gstins.filter(Boolean).map(g => g.substring(2, 12))].filter((v, i, a) => a.indexOf(v) === i),
      });
      if (r) created.push("it_portal");
    }

    // Create TRACES connector
    if (tracesEnabled && tans.some(Boolean)) {
      const r = await createConnector("traces", `TRACES — ${clientName}`, {
        tan_list: tans.filter(Boolean),
      });
      if (r) created.push("traces");
    }

    // Create MCA21 connector
    if (mcaEnabled && cin) {
      const r = await createConnector("mca21", `MCA21 — ${clientName}`, {
        mca_api_key: mcaApiKey,
      }, {
        cin_list: [cin],
        llpin_list: LLPIN_RE.test(cin.toUpperCase()) ? [cin.toUpperCase()] : [],
      });
      if (r) created.push("mca21");
    }

    // Also call the unified onboarding endpoint to register client in workspace registry
    try {
      const token = localStorage.getItem("cosfy_access") || "";
      const onboardRes = await fetch("/api/v1/ca/clients/onboard", {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          client_name:     clientName,
          pan:             pan.toUpperCase(),
          gstin:           gstins.filter(Boolean)[0] || null,
          entity_type:     "company",
          tally_enabled:   tallyEnabled,
          tally_url:       tallyUrl || null,
          tally_companies: companies.filter(Boolean),
          gst_enabled:     gstEnabled,
          it_enabled:      itEnabled,
          it_pan:          pan,
          traces_enabled:  tracesEnabled,
          mca_enabled:     mcaEnabled,
          mca_cin:         cin || null,
          financial_year:  "2024-25",
        }),
      });
      if (onboardRes.ok) {
        const onboardData = await onboardRes.json();
        console.log("Client onboarded:", onboardData.message);
      }
    } catch (e) {
      // Non-fatal — individual connectors already created above
      console.warn("Unified onboard call failed:", e.message);
    }

    setSyncStarted(true);
    setSaving(false);

    const clientData = {
      name: clientName, pan, gstins: gstins.filter(Boolean),
      tans: tans.filter(Boolean), cin, whatsapp,
      connectorsCreated: created,
    };

    onComplete?.(clientData);
  }, [clientName, pan, gstins, tans, cin, whatsapp, tallyUrl, companies,
      gstEnabled, gstins, itEnabled, tracesEnabled, mcaEnabled,
      gspClientId, gspSecret, eriClientId, eriSecret, mcaApiKey, tallyEnabled, onComplete]);

  if (!show) return null;

  const isMob = bp?.isMobile;
  const modalW = isMob ? "100vw" : 560;
  const modalH = isMob ? "100dvh" : "auto";
  const modalR = isMob ? 0 : 16;

  const STEP_LABELS = ["Identity", "Tally", "Portals", "Confirm"];

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 200,
      display: "flex", alignItems: isMob ? "flex-end" : "center", justifyContent: "center",
      backdropFilter: "blur(2px)",
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{
        width: modalW, maxHeight: isMob ? "96dvh" : "88vh",
        background: t?.sf || "#fff", borderRadius: isMob ? "16px 16px 0 0" : modalR,
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,.25)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${t?.bd || "#E3E8F0"}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: t?.tx || "#141C2E" }}>
                Add New Client
              </h2>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: t?.txM || "#718096" }}>
                Step {step + 1} of 4 — {STEP_LABELS[step]}
              </p>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t?.txM || "#718096", fontSize: 20, padding: "2px 4px", lineHeight: 1 }}>
              ✕
            </button>
          </div>
          <StepIndicator current={step} total={4} t={t || { p: "#1847C8", bd: "#E3E8F0" }} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

          {/* ── STEP 1: IDENTITY ── */}
          {step === 0 && (
            <div>
              <FieldRow label="Client / Firm Name" required error={clientName && errors.clientName}>
                <Input value={clientName} onChange={setClientName} placeholder="e.g. Sharma Trading Co." error={clientName && errors.clientName} />
              </FieldRow>

              <FieldRow label="PAN" error={pan && errors.pan} hint="10-character Permanent Account Number">
                <Input value={pan} onChange={v => setPan(v.toUpperCase())} placeholder="AABCS1429B" mono maxLength={10} error={pan && errors.pan} />
              </FieldRow>

              {/* GSTINs — dynamic list */}
              <FieldRow label="GSTIN(s)" hint="Add all GSTINs for this client across states">
                {gstins.map((g, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <Input
                      value={g} onChange={v => { const n = [...gstins]; n[i] = v.toUpperCase(); setGstins(n); }}
                      placeholder="27AABCS1429B1ZS" mono maxLength={15}
                      error={g && !validate.gstin(g) ? "Invalid GSTIN" : null}
                    />
                    {gstins.length > 1 && (
                      <button onClick={() => setGstins(gstins.filter((_, j) => j !== i))}
                        style={{ background: "#FEF2F2", border: "none", borderRadius: 8, padding: "0 10px", cursor: "pointer", color: "#EF4444", fontSize: 16 }}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setGstins([...gstins, ""])}
                  style={{ background: "none", border: `1px dashed ${t?.bd || "#E3E8F0"}`, borderRadius: 8, padding: "6px 14px", cursor: "pointer", color: t?.p || "#1847C8", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 2 }}>
                  + Add GSTIN
                </button>
              </FieldRow>

              {/* TANs */}
              <FieldRow label="TAN(s)" hint="Tax Deduction Account Number(s) for TDS filings">
                {tans.map((tn, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <Input
                      value={tn} onChange={v => { const n = [...tans]; n[i] = v.toUpperCase(); setTans(n); }}
                      placeholder="MUMD12345A" mono maxLength={10}
                      error={tn && !validate.tan(tn) ? "Invalid TAN" : null}
                    />
                    {tans.length > 1 && (
                      <button onClick={() => setTans(tans.filter((_, j) => j !== i))}
                        style={{ background: "#FEF2F2", border: "none", borderRadius: 8, padding: "0 10px", cursor: "pointer", color: "#EF4444", fontSize: 16 }}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setTans([...tans, ""])}
                  style={{ background: "none", border: `1px dashed ${t?.bd || "#E3E8F0"}`, borderRadius: 8, padding: "6px 14px", cursor: "pointer", color: t?.p || "#1847C8", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 2 }}>
                  + Add TAN
                </button>
              </FieldRow>

              <FieldRow label="CIN / LLPIN" hint="Company/LLP registration number (optional)" error={cin && errors.cin}>
                <Input value={cin} onChange={v => setCin(v.toUpperCase())} placeholder="U74999MH2020PTC123456" mono error={cin && errors.cin} />
              </FieldRow>

              <FieldRow label="WhatsApp Number" hint="For automated compliance alerts (with country code)" error={whatsapp && errors.whatsapp}>
                <Input value={whatsapp} onChange={setWhatsapp} placeholder="919820001234" mono />
              </FieldRow>
            </div>
          )}

          {/* ── STEP 2: TALLY ── */}
          {step === 1 && (
            <div>
              <p style={{ margin: "0 0 18px", fontSize: 13, color: t?.txM || "#718096", lineHeight: 1.6 }}>
                Connect Tally ERP on your server. The Jaanch AI connector pulls vouchers, ledgers, and stock data via the Tally XML API.
              </p>

              <FieldRow label="Tally Server URL" hint="Usually http://localhost:9000 on the Tally machine" required>
                <Input value={tallyUrl} onChange={setTallyUrl} placeholder="http://192.168.1.100:9000" mono />
              </FieldRow>

              <FieldRow label="Company Name(s) in Tally" hint="Exact name as shown in Tally — case sensitive">
                {companies.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    <Input
                      value={c} onChange={v => { const n = [...companies]; n[i] = v; setCompanies(n); }}
                      placeholder="Sharma Trading Co."
                    />
                    {companies.length > 1 && (
                      <button onClick={() => setCompanies(companies.filter((_, j) => j !== i))}
                        style={{ background: "#FEF2F2", border: "none", borderRadius: 8, padding: "0 10px", cursor: "pointer", color: "#EF4444", fontSize: 16 }}>×</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setCompanies([...companies, ""])}
                  style={{ background: "none", border: `1px dashed ${t?.bd || "#E3E8F0"}`, borderRadius: 8, padding: "6px 14px", cursor: "pointer", color: t?.p || "#1847C8", fontSize: 12, fontWeight: 600, width: "100%", marginTop: 2 }}>
                  + Add Company
                </button>
              </FieldRow>

              {/* Test connection */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                <button onClick={handleTestTally} disabled={tallyTesting || !tallyUrl}
                  style={{ background: tallyTesting ? "#F3F4F6" : "#EFF6FF", color: tallyTesting ? "#9CA3AF" : "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: tallyTesting ? "default" : "pointer", fontWeight: 600 }}>
                  {tallyTesting ? "⏳ Testing..." : "🔌 Test Connection"}
                </button>
                {tallyOk === true  && <span style={{ fontSize: 12, color: "#10B981", fontWeight: 700 }}>✅ Connected successfully</span>}
                {tallyOk === false && <span style={{ fontSize: 12, color: "#EF4444", fontWeight: 700 }}>❌ Could not connect — check URL and that Tally is running</span>}
              </div>

              <div style={{ marginTop: 18, padding: "12px 14px", background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, fontSize: 12, color: "#92400E", lineHeight: 1.6 }}>
                <b>Tally setup note:</b> Enable "Allow Remote Access" in Tally → Gateway of Tally → F12 → Advanced Configuration → Enable ODBC Server. Port 9000 by default.
              </div>
            </div>
          )}

          {/* ── STEP 3: PORTALS ── */}
          {step === 2 && (
            <div>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: t?.txM || "#718096", lineHeight: 1.6 }}>
                Toggle the portals you want to connect for this client. API credentials are per-practice (not per client) — enter once, used for all clients.
              </p>

              {/* ── GST Portal ── */}
              <ConnectorToggle icon="🏛️" label="GST Portal" sublabel="GSTR-1, 2B, 3B — no GSP license needed"
                enabled={gstEnabled} onToggle={() => setGstEnabled(v => !v)} color="#1D4ED8" />
              {gstEnabled && (
                <div style={{ padding: "14px", background: "#F8FAFD", border: "1px solid #E3E8F0", borderRadius: 8, marginBottom: 8, marginTop: -2 }}>
                  {/* Auth mode toggle */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                    {[["direct","🔑 Portal Login (recommended)"],["gsp","🏢 GSP License"]].map(([m,l]) => (
                      <button key={m} onClick={() => setGstAuthMode(m)} style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid",
                        background: gstAuthMode===m ? "#EFF6FF" : "#fff",
                        color: gstAuthMode===m ? "#1D4ED8" : "#6B7280",
                        borderColor: gstAuthMode===m ? "#BFDBFE" : "#E5E7EB",
                      }}>{l}</button>
                    ))}
                  </div>

                  {gstAuthMode === "direct" ? (<>
                    <div style={{ fontSize: 11, background: "#F0FDF4", color: "#065F46", border: "1px solid #BBF7D0", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>
                      ✅ No GSP license required. Use your GST portal login. Clients will receive an OTP on their registered mobile when you sync.
                    </div>
                    <FieldRow label="GST Portal Username">
                      <Input value={portalUser} onChange={setPortalUser} placeholder="Your GST Practitioner username / email" mono />
                    </FieldRow>
                    <FieldRow label="GST Portal Password">
                      <Input value={portalPass} onChange={setPortalPass} placeholder="Your GST portal password" mono />
                    </FieldRow>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                      Clients must have authorized you as their GST Practitioner on the GST portal.
                    </div>
                  </>) : (<>
                    <div style={{ fontSize: 11, background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>
                      ⚠ Requires GSP license (₹2Cr+ capital, ₹5Cr+ turnover). Use Direct mode for pilot.
                    </div>
                    <FieldRow label="GSP Client ID">
                      <Input value={gspClientId} onChange={setGspClientId} placeholder="From Masters India / IRIS GSP portal" mono />
                    </FieldRow>
                    <FieldRow label="GSP Client Secret">
                      <Input value={gspSecret} onChange={setGspSecret} placeholder="GSP OAuth2 secret" mono />
                    </FieldRow>
                  </>)}
                </div>
              )}

              {/* ── IT Portal ── */}
              <ConnectorToggle icon="📋" label="Income Tax Portal" sublabel="26AS, AIS, ITR status — use your ERIIN"
                enabled={itEnabled} onToggle={() => setItEnabled(v => !v)} color="#6D28D9" />
              {itEnabled && (
                <div style={{ padding: "14px", background: "#F8FAFD", border: "1px solid #E3E8F0", borderRadius: 8, marginBottom: 8, marginTop: -2 }}>
                  {/* Auth mode toggle */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                    {[["ca_eri","🔑 My ERIIN (recommended)"],["eri_app","🏢 ERI App Registration"]].map(([m,l]) => (
                      <button key={m} onClick={() => setItAuthMode(m)} style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid",
                        background: itAuthMode===m ? "#F5F3FF" : "#fff",
                        color: itAuthMode===m ? "#6D28D9" : "#6B7280",
                        borderColor: itAuthMode===m ? "#DDD6FE" : "#E5E7EB",
                      }}>{l}</button>
                    ))}
                  </div>

                  {itAuthMode === "ca_eri" ? (<>
                    <div style={{ fontSize: 11, background: "#F0FDF4", color: "#065F46", border: "1px solid #BBF7D0", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>
                      ✅ Use your firm's existing ERIIN. Most CA firms already have this from filing ITRs. Free to register at incometax.gov.in if not.
                    </div>
                    <FieldRow label="Your ERIIN" hint="e.g. ERIP000249">
                      <Input value={caEriin} onChange={setCaEriin} placeholder="ERIP000XXX" mono />
                    </FieldRow>
                    <FieldRow label="ERI Portal Password">
                      <Input value={caEriPass} onChange={setCaEriPass} placeholder="Your ERI login password" mono />
                    </FieldRow>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                      Clients must have activated your ERIIN on the IT portal (Authorized Partners → My e-Return Intermediary).
                    </div>
                  </>) : (<>
                    <div style={{ fontSize: 11, background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}>
                      ⚠ Requires separate ERI software registration with CBDT. Use ERIIN mode if you have one.
                    </div>
                    <FieldRow label="ERI Client ID">
                      <Input value={eriClientId} onChange={setEriClientId} placeholder="CBDT ERI registration ID" mono />
                    </FieldRow>
                    <FieldRow label="ERI Secret">
                      <Input value={eriSecret} onChange={setEriSecret} placeholder="ERI OAuth2 secret" mono />
                    </FieldRow>
                  </>)}
                </div>
              )}

              <ConnectorToggle icon="🧾" label="TRACES (TDS Portal)" sublabel="24Q/26Q returns, Form 16, TDS defaults"
                enabled={tracesEnabled} onToggle={() => setTracesEnabled(v => !v)} color="#B45309" />

              <ConnectorToggle icon="🏢" label="MCA21 (ROC)" sublabel="Company filings, directors, charges"
                enabled={mcaEnabled} onToggle={() => setMcaEnabled(v => !v)} color="#065F46" />
              {mcaEnabled && (
                <div style={{ padding: "12px 14px", background: "#F8FAFD", border: "1px solid #E3E8F0", borderRadius: 8, marginBottom: 8, marginTop: -2 }}>
                  <FieldRow label="MCA API Key" hint="From MCA developer portal">
                    <Input value={mcaApiKey} onChange={setMcaApiKey} placeholder="MCA developer portal API key" mono />
                  </FieldRow>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: CONFIRM ── */}
          {step === 3 && (
            <div>
              {!syncStarted ? (
                <>
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#065F46", marginBottom: 10 }}>✅ Ready to connect</div>
                    {[
                      ["Client", clientName],
                      ["PAN", pan || "—"],
                      ["GSTINs", gstins.filter(Boolean).join(", ") || "—"],
                      ["TANs", tans.filter(Boolean).join(", ") || "—"],
                      ["CIN/LLPIN", cin || "—"],
                      ["WhatsApp", whatsapp || "—"],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: "flex", gap: 8, fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: "#6B7280", width: 80, flexShrink: 0 }}>{k}:</span>
                        <span style={{ fontFamily: k !== "Client" ? "monospace" : "inherit", color: "#141C2E", fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Connectors to create:</div>
                    {[
                      companies.some(Boolean) && { icon: "📒", label: "Tally ERP", note: `${companies.filter(Boolean).length} company(ies)` },
                      gstEnabled && { icon: "🏛️", label: "GST Portal", note: `${gstins.filter(Boolean).length} GSTIN(s)` },
                      itEnabled && { icon: "📋", label: "IT Portal", note: pan },
                      tracesEnabled && tans.some(Boolean) && { icon: "🧾", label: "TRACES", note: `${tans.filter(Boolean).length} TAN(s)` },
                      mcaEnabled && cin && { icon: "🏢", label: "MCA21", note: cin },
                    ].filter(Boolean).map((c, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#F8FAFD", borderRadius: 6, marginBottom: 4 }}>
                        <span>{c.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{c.label}</span>
                        <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "monospace" }}>{c.note}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6, marginBottom: 20, padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8 }}>
                    Initial sync will begin immediately after clicking Connect. Tally crawl: ~2 min. GST/IT Portal: ~5 min per client depending on history depth.
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#141C2E", marginBottom: 8 }}>{clientName} connected!</div>
                  <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>Initial sync started. Data will appear in Search and Compliance within a few minutes.</div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <button onClick={onClose} style={{ background: "#1847C8", color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13, cursor: "pointer", fontWeight: 700 }}>
                      Done
                    </button>
                    <button onClick={() => { setStep(0); setClientName(""); setPan(""); setGstins([""]); setTans([""]); setCin(""); setWhatsapp(""); setTallyUrl("http://localhost:9000"); setCompanies([""]); setSyncStarted(false); setTallyOk(null); }}
                      style={{ background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
                      Add Another Client
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        {!syncStarted && (
          <div style={{ padding: "14px 20px", borderTop: `1px solid ${t?.bd || "#E3E8F0"}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: t?.sf2 || "#F8FAFD" }}>
            <button
              onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
              style={{ background: "none", border: `1px solid ${t?.bd || "#E3E8F0"}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer", color: t?.txM || "#718096", fontWeight: 600 }}>
              {step === 0 ? "Cancel" : "← Back"}
            </button>

            {step < 3 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={step === 0 && !step1Valid}
                style={{
                  background: (step === 0 && !step1Valid) ? "#E5E7EB" : "#1847C8",
                  color: (step === 0 && !step1Valid) ? "#9CA3AF" : "#fff",
                  border: "none", borderRadius: 8, padding: "8px 24px",
                  fontSize: 13, cursor: (step === 0 && !step1Valid) ? "default" : "pointer", fontWeight: 700,
                }}>
                {step === 2 ? "Review →" : "Next →"}
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={saving}
                style={{ background: saving ? "#E5E7EB" : "#10B981", color: saving ? "#9CA3AF" : "#fff", border: "none", borderRadius: 8, padding: "8px 24px", fontSize: 13, cursor: saving ? "default" : "pointer", fontWeight: 700 }}>
                {saving ? "⏳ Connecting..." : "🚀 Connect & Sync"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
